/**
 * Royalties — tokens for which the current Telegram user is the protocol-fee
 * recipient (set at deploy time). One "Забрать" tap per token settles BOTH
 * fee streams via the unified backend endpoint (CC-075):
 *
 *   - curve recipient TON fees — swept out of the bonding curve through the
 *     TEMP_CUSTODY wallet (for pending-earmark tokens);
 *   - LP-fee jettons — paid from the BLINKR_JETTON_ACCUMULATOR wallet.
 *
 * Both are backend-signed payouts to the wallet the user connected via
 * POST /api/me — no TonConnect signature is needed. The two sides settle
 * independently, so the result can be a partial success.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRoyalties } from '../hooks/useRoyalties';
import { useTonWallet } from '../hooks/useTonWallet';
import { ConnectButton } from '../components/ConnectButton';
import { claimAll, type RoyaltyToken } from '../lib/api';
import { formatTon } from '../lib/ton';
import { formatJetton } from '../lib/displayMath';
import { claimAllState } from '../lib/royaltyClaim';

export function RoyaltiesRoute() {
  const { state, refresh } = useRoyalties();
  const wallet = useTonWallet();
  const walletReady = wallet.state.kind === 'ready';

  const [busyMaster, setBusyMaster] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const onClaim = async (token: RoyaltyToken) => {
    if (!walletReady) {
      setToast('Сначала подключи кошелёк.');
      return;
    }
    setBusyMaster(token.masterAddr);
    setToast(null);
    try {
      const res = await claimAll(token.id);
      const parts: string[] = [];
      if (res.ton.status === 'claimed' && res.ton.amount) {
        parts.push(`${formatTon(BigInt(res.ton.amount), 4)} TON`);
      }
      if (res.jetton.status === 'claimed' && res.jetton.amount) {
        parts.push(
          `${formatJetton(BigInt(res.jetton.amount), token.jettonDecimals)} $${token.jettonSymbol}`,
        );
      }
      if (parts.length > 0 && res.partial) {
        setToast(
          `✅ Получено ${parts.join(' и ')}. Часть не удалась — повтори попытку.`,
        );
      } else if (parts.length > 0) {
        setToast(`✅ Получено ${parts.join(' и ')} — в пути на твой кошелёк.`);
      } else {
        setToast('✅ Готово.');
      }
      void refresh();
      setTimeout(() => setToast(null), 6000);
    } catch (err: any) {
      setToast(
        `❌ ${String(err?.message ?? 'не удалось забрать комиссии').slice(0, 200)}`,
      );
    } finally {
      setBusyMaster(null);
    }
  };

  return (
    <div className="container vgap-16">
      <header className="between">
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Комиссии</h1>
          <div className="muted">Комиссии от токенов, запущенных для тебя.</div>
        </div>
        <button
          className="secondary"
          onClick={() => refresh()}
          style={{ padding: '6px 12px', fontSize: 13 }}
        >
          Обновить
        </button>
      </header>

      {!walletReady && (
        <div className="card vgap-8">
          <div>Подключи TON-кошелёк, чтобы забрать комиссии.</div>
          <ConnectButton />
        </div>
      )}

      {state.kind === 'loading' && <Skeletons />}

      {state.kind === 'error' && (
        <div className="error-text">Не удалось загрузить комиссии.</div>
      )}

      {state.kind === 'loaded' && state.tokens.length === 0 && (
        <div className="muted">
          Пока комиссий нет. Запусти токен (бот по умолчанию делает тебя
          получателем комиссий) или попроси кого-нибудь тегнуть бота{' '}
          <code>$TICKER for {getMyHandleOrId()}</code>.
        </div>
      )}

      {state.kind === 'loaded' &&
        state.tokens.map((t) => (
          <RoyaltyRow
            key={t.masterAddr}
            token={t}
            walletReady={walletReady}
            busy={busyMaster === t.masterAddr}
            thresholdNano={state.claimThresholdNano}
            gasNano={state.claimGasNano}
            onClaim={() => onClaim(t)}
          />
        ))}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function RoyaltyRow({
  token,
  walletReady,
  busy,
  thresholdNano,
  gasNano,
  onClaim,
}: {
  token: RoyaltyToken;
  walletReady: boolean;
  busy: boolean;
  thresholdNano: bigint;
  gasNano: bigint;
  onClaim: () => void;
}) {
  const tonFees = BigInt(token.accruedTonFees);
  const jettonFees = BigInt(token.accruedJettonFees);
  // CC-075/CC-077 — one button settles both. Pure helper (unit-tested).
  const claimState = claimAllState({
    tonAmount: tonFees,
    jettonAmount: jettonFees,
    thresholdNano,
    walletConnected: walletReady,
    busy,
  });

  // "Доступно: X TON · Y $TICKER" — only the non-zero parts.
  const availParts: string[] = [];
  if (tonFees > 0n) availParts.push(`${formatTon(tonFees, 4)} TON`);
  if (jettonFees > 0n) {
    availParts.push(
      `${formatJetton(jettonFees, token.jettonDecimals)} $${token.jettonSymbol}`,
    );
  }

  // CC-077 — TON-side claim economics for the per-row hint.
  const tonEligible = tonFees > 0n && tonFees >= thresholdNano;
  const netTon = tonFees > gasNano ? tonFees - gasNano : 0n;
  const deficit = thresholdNano > tonFees ? thresholdNano - tonFees : 0n;

  return (
    <div className="card row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <img
        className="token-img"
        src={`https://gateway.pinata.cloud/ipfs/${token.imageCid}`}
        alt={token.ticker}
        loading="lazy"
      />
      <div style={{ flex: 1 }}>
        <Link to={`/token/${token.masterAddr}`}>
          <strong>${token.ticker}</strong>
        </Link>
        <div className="muted" style={{ fontSize: 13 }}>
          {claimState.kind === 'none'
            ? 'Пока нет комиссий'
            : `Доступно: ${availParts.join(' · ')}`}
        </div>
        {/* CC-077 — sub-threshold TON: tell the user how much more is needed. */}
        {claimState.kind === 'below_threshold' && (
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Нужно ≥ {formatTon(thresholdNano, 2)} TON для выплаты — накопи ещё{' '}
            {formatTon(deficit, 4)} TON.
          </div>
        )}
        {/* CC-077 — claimable TON: show the net amount after the gas fee. */}
        {claimState.kind !== 'below_threshold' &&
          claimState.kind !== 'none' &&
          tonEligible && (
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              После комиссии за газ: ≈ {formatTon(netTon, 4)} TON
            </div>
          )}
        {claimState.kind === 'connect' && (
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Подключи кошелёк чтобы получить
          </div>
        )}
      </div>
      <button
        disabled={claimState.kind !== 'claimable' && claimState.kind !== 'busy'}
        onClick={onClaim}
        style={{ minWidth: 132 }}
      >
        {claimState.kind === 'busy' ? (
          <>
            <span className="spinner" /> Отправляется…
          </>
        ) : claimState.kind === 'connect' ? (
          'Подключи кошелёк'
        ) : claimState.kind === 'below_threshold' ? (
          `Накопи ещё ${formatTon(deficit, 4)} TON`
        ) : claimState.kind === 'none' ? (
          'Нет комиссий'
        ) : (
          'Забрать'
        )}
      </button>
    </div>
  );
}

function Skeletons() {
  return (
    <>
      {[0, 1].map((i) => (
        <div key={i} className="card row">
          <div className="skeleton" style={{ width: 48, height: 48 }} />
          <div style={{ flex: 1 }}>
            <div className="skeleton" style={{ height: 14, width: '40%' }} />
            <div className="skeleton" style={{ height: 6, width: '100%', marginTop: 12 }} />
          </div>
        </div>
      ))}
    </>
  );
}

/** Creator's @handle for the empty-state hint, or their numeric id. */
function getMyHandleOrId(): string {
  const u = window.Telegram?.WebApp?.initDataUnsafe?.user;
  if (u?.username) return `@${u.username}`;
  if (u?.id) return String(u.id);
  return '@you';
}
